import { useEffect, useMemo, useState } from '../../vendor/react';
import { getBusinessProfile } from '../../config/businessProfiles';
import { useWorkspace } from '../workspace/useWorkspace';
import { adjustInventory, duplicateProduct, ensureCatalog, exportProductsCsv, getCatalog, importProductsCsv, productWithInventory, saveCategory, saveProduct, saveVariant, setProductArchived, subscribeCatalog, type InventoryAdjustment, type ProductDraft } from './catalog-store';
import type { ProductVariant } from '../../types/domain';

export function useCatalog() {
  const workspace = useWorkspace();
  const businessId = workspace.business?.id ?? '';
  const branchId = workspace.branch?.id ?? '';
  const profile = getBusinessProfile(workspace.business?.profileId);
  const [revision, setRevision] = useState(0);

  useEffect(() => subscribeCatalog(() => setRevision((value) => value + 1)), []);
  const catalog = useMemo(() => {
    if (!businessId) return null;
    const branchIds = workspace.workspace?.branches.filter((item) => item.businessId === businessId).map((item) => item.id) ?? [];
    return getCatalog(businessId) ?? ensureCatalog(businessId, branchIds, profile.id);
  }, [businessId, profile.id, workspace.workspace?.branches, revision]);
  const products = useMemo(() => catalog && branchId ? productWithInventory(catalog, branchId) : [], [catalog, branchId]);

  return {
    ...workspace,
    profile,
    catalog,
    products,
    categories: catalog?.categories ?? [],
    variants: catalog?.variants ?? [],
    inventory: catalog?.inventory ?? [],
    movements: catalog?.movements ?? [],
    actions: {
      saveCategory: (input: { id?: string; name: string; color: string }) => saveCategory(businessId, input),
      saveProduct: (draft: ProductDraft) => saveProduct(businessId, draft),
      duplicateProduct: (productId: string) => duplicateProduct(businessId, productId),
      archiveProduct: (productId: string) => setProductArchived(businessId, productId, true),
      restoreProduct: (productId: string) => setProductArchived(businessId, productId, false),
      saveVariant: (productId: string, input: Omit<ProductVariant, 'id' | 'productId'> & { id?: string }) => saveVariant(businessId, productId, input),
      adjustInventory: (input: Omit<InventoryAdjustment, 'branchId' | 'createdBy'> & { branchId?: string }) => adjustInventory(businessId, { ...input, branchId: input.branchId ?? branchId, createdBy: workspace.session?.user.id ?? 'unknown' }),
      exportCsv: () => exportProductsCsv(catalog?.products ?? []),
      importCsv: (csv: string) => importProductsCsv(businessId, branchId, csv, workspace.session?.user.id ?? 'unknown'),
    },
  };
}
